import { useRef, useState, useEffect } from "react";
import { AnimatePresence, motion } from "motion/react";
import { Typography } from "../Typography/Typography";
import { cva, type VariantProps } from "class-variance-authority";

import * as styles from "./PillNotice.module.scss";

type PillItem = {
  id: string;
  startValue: number;
  latestValue: number;
  expiresAt: number;
  isExiting: boolean;
};

const pillVariants = cva(styles.pill, {
  variants: {
    variant: {
      primary: styles.primary,
      promo: styles.promo,
    },
  },
  defaultVariants: {
    variant: "primary",
  },
});

export type PillNoticeProps = VariantProps<typeof pillVariants> & {
  value: number;
  formatValue?: (delta: number) => string;
  isLoading?: boolean;
  direction?: "UP" | "DOWN";
  offsetTop: string;
  offsetRight: string;
};

const ANIMATION_DURATION = 2.8;
const ANIMATION_EXIT_DURATION = 0.2;
const DELAY_BEFORE_EXIT = 1;

export function PillNotice({
  value,
  formatValue = (delta) => String(delta),
  isLoading,
  direction = "UP",
  variant,
  offsetTop,
  offsetRight,
}: PillNoticeProps) {
  const firstRef = useRef(true);
  const previousValueRef = useRef(0);
  const idRef = useRef(0);
  const pendingTimersRef = useRef<Set<number>>(new Set());

  const [items, setItems] = useState<PillItem[]>([]);
  const itemsRef = useRef<PillItem[]>(items);
  itemsRef.current = items;

  useEffect(() => {
    return () => {
      // Clear all pending timers
      pendingTimersRef.current.forEach(window.clearTimeout);
      pendingTimersRef.current.clear();

      // Activity hide/show re-runs effects; resync baseline instead of diffing stale value
      firstRef.current = true;
      setItems([]);
    };
  }, []);

  const createItem = (startValue: number, latestValue: number): PillItem => {
    idRef.current += 1;

    return {
      id: String(idRef.current),
      startValue,
      latestValue,
      expiresAt: Date.now() + DELAY_BEFORE_EXIT * 1000,
      isExiting: false,
    };
  };

  const markItemExiting = (id: string, force = false) => {
    setItems((current) => {
      const target = current.find((item) => item.id === id);
      // skip if missing, already exiting, or expiry was extended after the timer was scheduled
      if (
        !target ||
        target.isExiting ||
        (!force && target.expiresAt > Date.now())
      ) {
        return current;
      }
      return current.map((item) =>
        item.id === id ? { ...item, isExiting: true } : item,
      );
    });

    const timer = window.setTimeout(() => {
      pendingTimersRef.current.delete(timer);
      // Only remove the item if it was actually marked as exiting
      setItems((current) =>
        current.filter((item) => !(item.id === id && item.isExiting)),
      );
    }, ANIMATION_EXIT_DURATION * 1000);

    pendingTimersRef.current.add(timer);
  };

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (firstRef.current) {
      firstRef.current = false;
      previousValueRef.current = value;
      return;
    }

    const previousValue = previousValueRef.current;

    if (value === previousValue) {
      return;
    }

    const now = Date.now();
    const activeItem = itemsRef.current.find((item) => !item.isExiting);

    if (!activeItem) {
      if (value - previousValue > 0) {
        setItems((current) => [...current, createItem(previousValue, value)]);
      }
    } else if (value - activeItem.startValue <= 0) {
      markItemExiting(activeItem.id, true);
    } else {
      setItems((current) =>
        current.map((item) =>
          item.id === activeItem.id
            ? {
                ...item,
                latestValue: value,
                expiresAt: now + DELAY_BEFORE_EXIT * 1000,
              }
            : item,
        ),
      );
    }

    previousValueRef.current = value;
  }, [value, isLoading]);

  useEffect(() => {
    const activeItem = items.find((item) => !item.isExiting);

    if (!activeItem) {
      return;
    }

    const timeoutMs = Math.max(activeItem.expiresAt - Date.now(), 0);

    const timer = window.setTimeout(() => {
      markItemExiting(activeItem.id);
    }, timeoutMs);

    return () => window.clearTimeout(timer);
  }, [items]);

  const yValue = direction === "UP" ? -8 : 8;

  return (
    <div className={styles.container}>
      <AnimatePresence initial={false}>
        {items.map((item) => {
          const delta = item.latestValue - item.startValue;
          const formattedDelta = formatValue(delta);

          return (
            <motion.div
              key={item.id}
              style={{
                transformOrigin: direction === "UP" ? "50% 100%" : "50% 0",
                position: "absolute",
                top: offsetTop,
                right: offsetRight,
                pointerEvents: "none",
              }}
              initial={{ y: 0, scale: 0.2, opacity: 0 }}
              animate={
                item.isExiting
                  ? {
                      y: yValue,
                      scale: 1.5,
                      opacity: 0,
                      transition: {
                        duration: ANIMATION_EXIT_DURATION,
                      },
                    }
                  : {
                      y: [0, 0, yValue, yValue],
                      scale: [0.2, 1, 1, 1],
                      opacity: [0, 1, 1, 1],
                      transition: {
                        duration: ANIMATION_DURATION,
                        times: [0, 0.05, 0.2, 1],
                        ease: "linear",
                      },
                    }
              }
            >
              <div className={pillVariants({ variant })}>
                <motion.div
                  key={formattedDelta}
                  initial={{ scale: 1 }}
                  animate={{
                    scale: [1, 1.1, 1],
                  }}
                  transition={{ duration: 0.3 }}
                >
                  <Typography component="span" variant="h6" noSpacing>
                    <span className={styles.text}>+{formattedDelta}</span>
                  </Typography>
                </motion.div>
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}
